import { APP_VERSION } from './app-version';
import { guardedReload } from './chunk-reload';

/**
 * Written into the dist root at build time (see vite.config.ts) and served by nginx's
 * `location /`, which sets `no-cache` — unlike `/assets/`, whose content-hashed files are
 * immutable for a year.
 */
const BUILD_MANIFEST = '/build.json';

/**
 * Reloads a tab that is provably running a build the server has replaced.
 *
 * The failure this exists for: `/assets/` is cached `immutable`, so a tab left open across a
 * deploy keeps its old shell and lazily imports old chunks straight out of the browser cache.
 * They load perfectly — nothing 404s, so `isChunkLoadError` never sees anything — and then run
 * against an API that has moved on. On 2026-09-07 that meant reading a response field renamed
 * hours earlier, on two machines at once.
 *
 * Deliberately only ever called once a tab has already thrown. A newer build on the server is
 * not on its own a reason to interrupt someone mid-form; a newer build *plus* an error they were
 * about to be shown anyway is.
 *
 * Answers false for anything short of proof — a dev build, an unreachable manifest, a version
 * that isn't a usable string — because the cost of a wrong true is a reload loop that hides a
 * genuine bug behind it.
 */
export async function reloadIfStaleBuild(): Promise<boolean> {
  if (!APP_VERSION || APP_VERSION === 'dev') return false;

  let served: unknown;
  try {
    const response = await fetch(BUILD_MANIFEST, { cache: 'no-store' });
    if (!response.ok) return false;
    served = ((await response.json()) as { version?: unknown }).version;
  } catch {
    // Offline, or the manifest is missing on an older deploy. Either way this tells us nothing.
    return false;
  }

  if (typeof served !== 'string' || !served || served === APP_VERSION) return false;

  // False when the cooldown refuses — the caller must not report a recovery that isn't coming.
  return guardedReload();
}
